// src/App.jsx
import React, { useState, useEffect } from "react";
import { questionSteps, respondentStep } from "./questions";
import "./App.css";

/** ===== 設定 ===== */
const TERMS_VERSION = "v1.0";
const IS_DEV = process.env.NODE_ENV !== "true"; // ← 開発時：true　本番時：production

/** ===== Cookie保存 ===== */
function setConsentAccepted() {
  if (IS_DEV) return; // ← 開発時は保存しない

  const value = `accepted:${TERMS_VERSION}`;
  document.cookie = `cookie-consent=${value}; path=/; max-age=31536000; samesite=lax`;

  try {
    localStorage.setItem("termsVersion", TERMS_VERSION);
    localStorage.setItem("termsAcceptedAt", new Date().toISOString());
  } catch (_) {}
}

/** ===== 同意チェック ===== */
function hasConsent() {
  if (IS_DEV) return false; // ← 開発時は常に規約表示
  return document.cookie.includes(`cookie-consent=accepted:${TERMS_VERSION}`);
}

/** ===== メタ情報 ===== */
function getConsentMeta() {
  return {
    termsVersion: localStorage.getItem("termsVersion") || TERMS_VERSION,
    termsAcceptedAt:
      localStorage.getItem("termsAcceptedAt") || new Date().toISOString(),
  };
}

export default function App() {
  const [consented, setConsented] = useState(false);
  const [agreeChecked, setAgreeChecked] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setConsented(hasConsent());
  }, []);

  const handleSelect = (code, value, type) => {
    setAnswers((prev) => {
      if (type === "checkbox") {
        const current = prev[code] || [];
        return {
          ...prev,
          [code]: current.includes(value)
            ? current.filter((v) => v !== value)
            : [...current, value],
        };
      }
      return { ...prev, [code]: value };
    });
  };

  const handleSubmit = async () => {
    const meta = getConsentMeta();

    const res = await fetch("/api/submit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ answers, meta }),
    });

    const data = await res.json();
    if (!res.ok || !data.success) {
      alert("❌ 登録エラー");
      return;
    }

    setSubmitted(true);
  };

  const allSteps = [respondentStep, ...questionSteps];
  const totalSteps = allSteps.length;
  const progress = ((step + 1) / totalSteps) * 100;

  const getLabel = (code) => {
    for (const s of allSteps) {
      const found = s.questions.find((q) => q.code === code);
      if (found) return found.label;
    }
    return code;
  };

  return (
    <div className="app-container">

      {/* ===== ヘッダー ===== */}
      <div className="header">
        <div className="header-inner">
          <img src="/logo.svg" alt="KT Partners" className="logo" />
          <h1 className="main-title">人事評価制度アセスメント</h1>
        </div>

        <p className="sub-title">
          約3分で貴社の評価制度の成熟度を診断します
        </p>
      </div>
      
      {/* ===== 完了 ===== */}
      {submitted && (
        <h2 className="thank-you">✅ ご回答ありがとうございました！</h2>
      )}

      {/* ===== 利用規約 ===== */}
      {!submitted && !consented && (
        <>
          <h2 className="step-title">利用規約</h2>

          <div className="question">
            <ul>
              <li>入力情報は安全に管理されます</li>
              <li>診断結果送付のために利用します</li>
              <li>目的外利用は行いません</li>
            </ul>

            <details>
              <summary>利用規約の全文を表示</summary>
              <div style={{ padding: 16, lineHeight: 1.8 }}>
            <h3 style={{ marginBottom: 20 }}>
              利用規約（人事評価制度アセスメント回答フォーム）
            </h3>

            <p style={{ marginBottom: 16 }}>
              本アセスメント回答フォーム（以下「本フォーム」といいます）は、
              企業における人事評価制度の運用状況および制度成熟度の把握を
              目的として提供されます。
            </p>

            {/* --- 第1条 --- */}
            <div style={{ marginBottom: 20 }}>
              <p style={{ fontWeight: "bold", marginBottom: 8 }}>
                第1条（取得する情報）
              </p>
              <ul style={{ paddingLeft: 20 }}>
                <li>回答者氏名</li>
                <li>連絡先メールアドレス</li>
                <li>連絡先電話番号</li>
                <li>人事評価制度に関する回答内容</li>
              </ul>
             </div>

            {/* --- 第2条 --- */}
            <div style={{ marginBottom: 20 }}>
              <p style={{ fontWeight: "bold", marginBottom: 8 }}>
                第2条（利用目的）
              </p>
              <ul style={{ paddingLeft: 20 }}>
                <li>診断結果の送付</li>
                <li>本アセスメントに関するご連絡</li>
                <li>制度改善提案のご案内</li>
                <li>サービス向上のための分析</li>
              </ul>
            </div>

            {/* --- 第3条 --- */}
            <div style={{ marginBottom: 20 }}>
              <p style={{ fontWeight: "bold", marginBottom: 8 }}>
                第3条（第三者提供）
              </p>
              <p>
                法令に基づく場合を除き、取得した個人情報を
                ご本人の同意なく第三者へ提供することはありません。
              </p>
            </div>

            {/* --- 第4条 --- */}
            <div style={{ marginBottom: 20 }}>
              <p style={{ fontWeight: "bold", marginBottom: 8 }}>
                第4条（安全管理）
              </p>
              <p>
                取得した情報は、適切な安全管理措置を講じたうえで管理し、
                不正アクセス、漏えい、滅失または毀損の防止に努めます。
              </p>
            </div>

            {/* --- 第5条 --- */}
            <div style={{ marginBottom: 20 }}>
              <p style={{ fontWeight: "bold", marginBottom: 8 }}>
                第5条（回答の任意性）
              </p>
              <p>
                本フォームへの回答は任意です。
                ただし、必要事項をご入力いただけない場合、
                診断結果の送付ができない場合があります。
              </p>
            </div>

            {/* --- 第6条 --- */}
            <div style={{ marginBottom: 20 }}>
              <p style={{ fontWeight: "bold", marginBottom: 8 }}>
                第6条（規約の変更）
              </p>
              <p>
                本規約は必要に応じて変更されることがあります。
                変更後の内容は本フォーム上に掲載した時点で効力を生じます。
              </p>
            </div>

            {/* --- 第7条 --- */}
            <div style={{ marginBottom: 20 }}>
              <p style={{ fontWeight: "bold", marginBottom: 8 }}>
                第7条（同意）
              </p>
              <p>
                「利用規約に同意する」にチェックした時点で、
                本規約に同意したものとみなします。
              </p>
            </div>

            <hr style={{ margin: "24px 0" }} />

            <p>
              制定日：2026年02月01日<br />
              提供者：
              <a
                href="https://ktpartners.co.jp/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#2563eb", textDecoration: "underline" }}
              >
                株式会社KT Partners
              </a>
            </p>
              </div>
            </details>
          </div>

          <label className="question">
            <input
              type="checkbox"
              checked={agreeChecked}
              onChange={(e) => setAgreeChecked(e.target.checked)}
            />
            利用規約に同意します
          </label>

          <button
            className="nav-button"
            disabled={!agreeChecked}
            onClick={() => {
              setConsentAccepted();
              setConsented(true);
              setStep(0);
            }}
          >
            同意して回答へ進む
          </button>
        </>
      )}

      {/* ===== 確認画面 ===== */}
      {!submitted && consented && step === totalSteps && (
        <>
          <h2 className="step-title">送信前の確認</h2>

          <table className="confirm-table">
            <tbody>
              {Object.entries(answers).map(([k, v]) => (
                <tr key={k}>
                  <td>{getLabel(k)}</td>
                  <td>{Array.isArray(v) ? v.join(", ") : v}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <button className="nav-button" onClick={() => setStep(step - 1)}>
            ← 修正する
          </button>

          <button className="nav-button" onClick={handleSubmit}>
            登録
          </button>
        </>
      )}

      {/* ===== フォーム ===== */}
      {!submitted && consented && step < totalSteps && (
        <>
          <div className="progress-container">
            <div className="progress-bar" style={{ width: `${progress}%` }} />
          </div>

          <p className="progress-text">
            ステップ {step + 1} / {totalSteps}
          </p>

          <h2 className="step-title">{allSteps[step].title}</h2>

          {allSteps[step].questions.map((q) => (
            <div key={q.code} className="question">
              <p className="question-label">{q.label}</p>

              {(q.type === "text" ||
                q.type === "email" ||
                q.type === "tel") && (
                <input
                  type={q.type}
                  value={answers[q.code] || ""}
                  onChange={(e) =>
                    handleSelect(q.code, e.target.value, q.type)
                  }
                  className="text-input"
                />
              )}

              {q.options && (
                <div className="option-group-vertical">
                  {q.options.map((opt) => {
                    const selected =
                      q.type === "checkbox"
                        ? (answers[q.code] || []).includes(opt)
                        : answers[q.code] === opt;

                    return (
                      <div
                        key={opt}
                        className={`card-option ${
                          selected ? "selected" : ""
                        }`}
                        onClick={() =>
                          handleSelect(q.code, opt, q.type)
                        }
                      >
                        {opt}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}

          <div className="navigation">
            {step > 0 && (
              <button
                className="nav-button"
                onClick={() => setStep(step - 1)}
              >
                戻る
              </button>
            )}

            <button
              className="nav-button"
              onClick={() => setStep(step + 1)}
            >
              次へ進む
            </button>
          </div>
        </>
      )}

      {/* ===== フッター ===== */}
      <div className="footer">
        <p>© 2026 KT Partners Co., Ltd. All rights reserved.</p>
      </div>
    </div>
  );
}
